import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  CustomRewardCurrency,
  JoonUIColor,
  Modal,
  SPACING,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import { useRef, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { screenTimeFTUXItems } from "../../constants"
import { useFamilyQuery } from "../../networking/queries"
import { useAddRewardMutation } from "../../networking/rewards/mutations"
import { useDoterAIInfoModalStore } from "../../pages/family/manageGameplay/doterChat/useDoterAIInfoModalStore"
import { FamilyQuerySelect } from "../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"

// Translation keys for screenTimeFTUXItems (constants holds the icons + English
// fallback); aligned by index so the displayed text is localized at render.
const SCREENTIME_ITEM_KEYS = [
  { title: "SCREENTIME_ITEM1_TITLE", description: "SCREENTIME_ITEM1_DESC" },
  { title: "SCREENTIME_ITEM2_TITLE", description: "SCREENTIME_ITEM2_DESC" },
  { title: "SCREENTIME_ITEM3_TITLE", description: "SCREENTIME_ITEM3_DESC" },
] as const

interface ManageScreenTimeFTUXModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

export const useManageScreenTimeFTUXModalStore =
  create<ManageScreenTimeFTUXModalStore>((set) => ({
    isOpen: false,
    onOpen: () => {
      trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_SCREEN_TIME_INFORMATIONAL)
      set({ isOpen: true })
    },
    onClose: () => set({ isOpen: false }),
  }))

const ManageScreenTimeFTUXModal = () => {
  const [isAtBottom, setIsAtBottom] = useState(true)
  const { isOpen, onClose } = useManageScreenTimeFTUXModalStore()
  const { onOpen: onOpenDoterAIInfoModal } = useDoterAIInfoModalStore()
  const addRewardMutation = useAddRewardMutation()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current
      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 5)
    }
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener("scroll", handleScroll)
      handleScroll()
      return () => container.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const handleSkip = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.TOGGLE_ONBOARDING_SCREENTIME, {
      enabled: false,
    })
    onOpenDoterAIInfoModal(true)
    onClose()
  }

  const handleTurnOn = async () => {
    await addRewardMutation.mutateAsync({
      title: "Screen Time",
      cost: 4,
      emoji: "📱",
      user_ids: children?.map((child) => child.user.id) ?? [],
      purchasable_from_joonity: true,
      currency: CustomRewardCurrency.MINUTE,
    })
    trackAnalyticEvent(ANALYTIC_EVENTS.TOGGLE_ONBOARDING_SCREENTIME, {
      enabled: true,
    })
    trackAnalyticEvent(ANALYTIC_EVENTS.CREATE_CUSTOM_REWARD, {
      currency: CustomRewardCurrency.MINUTE,
    })
    onOpenDoterAIInfoModal(true)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      mandatory
      style={{ width: "min(450px, 100vw)" }}
      animate
    >
      <div
        ref={scrollContainerRef}
        className="w-full flex flex-col overflow-y-auto"
      >
        <div className="w-full flex flex-col p-6 gap-3 pb-4">
          <Typography variant="h3" textAlign="center">
            {t("SCREENTIME_FTUX_TITLE")}
          </Typography>
          <Typography variant="bodySmall" textAlign="center">
            {t("SCREENTIME_FTUX_SUBTITLE")}
          </Typography>
        </div>
        <div
          className="w-full flex items-center justify-center"
          style={{
            userSelect: "none",
            pointerEvents: "none",
            borderBottom: `1px solid ${JoonUIColor.border.default}`,
          }}
        >
          <img
            src="/images/lock_apps.png"
            alt="screen time controls"
            style={{ width: "100%", height: "auto" }}
          />
        </div>

        <Typography variant="bodyBold" className="p-6 pb-4">
          {t("SCREENTIME_FTUX_LOCK_TITLE")}
        </Typography>
        {screenTimeFTUXItems.map((item, index) => (
          <div key={index} className="flex flex-row gap-1 px-6">
            <FontAwesomeIcon
              icon={item.icon}
              color={JoonUIColor.icon.accent}
              fontSize={SPACING.space4}
            />
            <div key={index} className="flex flex-col gap-1">
              <Typography
                variant="bodySmallBold"
                color={JoonUIColor.text.primary}
              >
                {t(SCREENTIME_ITEM_KEYS[index].title)}
              </Typography>
              <Typography variant="bodySmall" color={JoonUIColor.text.primary}>
                {t(SCREENTIME_ITEM_KEYS[index].description)}
              </Typography>
            </div>
          </div>
        ))}
        <Typography
          variant="bodyXSmall"
          className="p-6 pb-4"
          textAlign="center"
        >
          {t("SCREENTIME_FTUX_MODIFY_LATER")}
        </Typography>
        <div
          className="w-full flex flex-col gap-2 justify-center items-center bg-white p-6 py-4"
          style={{
            position: "sticky",
            bottom: 0,
            boxShadow: isAtBottom ? "none" : "0 -4px 12px rgba(0, 0, 0, 0.1)",
            transition: "box-shadow 0.2s ease-in-out",
          }}
        >
          <Button
            text={t("SCREENTIME_FTUX_TURN_ON")}
            onClick={handleTurnOn}
            isLoading={addRewardMutation.isLoading}
            fullWidth
          />
          <TextButton onClick={handleSkip} style={{ width: "fit-content" }}>
            <Typography variant="body" color={JoonUIColor.text.primaryAccent}>
              {t("SCREENTIME_FTUX_SKIP")}
            </Typography>
          </TextButton>
        </div>
      </div>
    </Modal>
  )
}

export default ManageScreenTimeFTUXModal
